import { IconPencil } from '@tabler/icons-react';

import { getInvoiceInformation } from '@/actions/invoice-information';
import { InvoicingType } from '@/prisma/types';
import { ExtendedUser } from '@/prisma/types';
import { ActionFnData } from '@/types/response-types';
import { IconButton } from '@/ui-lib/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui-lib/tooltip';

interface InfoFieldProps {
    label: string;
    value: string;
}

const InfoField = ({ label, value }: InfoFieldProps) => {
    return (
        <div className="col-span-1">
            <h6 className="font-semibold text-sm text-neutral-100">{label}</h6>
            <Tooltip>
                <TooltipTrigger asChild>
                    <p className="text-neutral-400 truncate">{value}</p>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="max-w-xs">
                    <p className="break-words">{value}</p>
                </TooltipContent>
            </Tooltip>
        </div>
    );
};

interface InvoiceInformationProps {
    user: Pick<ExtendedUser, 'email'>;
    invoiceInformation: Pick<
        NonNullable<ActionFnData<typeof getInvoiceInformation>>,
        'name' | 'invoiceEmail' | 'vatNumber' | 'invoicingType'
    > & {
        address?: {
            street?: string | null;
            city?: string | null;
            country?: {
                displayName?: string | null;
                alpha2Code?: string | null;
            } | null;
        } | null;
    };
    onEditClick?: () => void;
    hideUnsetFields?: boolean;
}

export const InvoiceInformationComponent = ({
    user,
    invoiceInformation,
    onEditClick,
    hideUnsetFields,
}: InvoiceInformationProps) => {
    const isCompany = invoiceInformation.invoicingType === InvoicingType.COMPANY;

    const emailValue = invoiceInformation.invoiceEmail || user.email || '-';

    return (
        <TooltipProvider>
            <div className="flex flex-col justify-center w-full">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-semibold text-neutral-100">Invoice Details</h3>
                    {onEditClick && (
                        <IconButton icon={IconPencil} onClick={onEditClick} title="Edit Invoice Information" />
                    )}
                </div>
                <div className="grid grid-cols-2 w-full gap-x-8 gap-y-4">
                    {hideUnsetFields && !invoiceInformation.name ? null : (
                        <InfoField
                            label={isCompany ? 'Company Name' : 'Full Name'}
                            value={invoiceInformation.name || '-'}
                        />
                    )}
                    {hideUnsetFields && !invoiceInformation.address?.street ? null : (
                        <InfoField label="Street" value={invoiceInformation.address?.street || '-'} />
                    )}
                    {hideUnsetFields && !invoiceInformation.address?.city ? null : (
                        <InfoField label="City" value={invoiceInformation.address?.city || '-'} />
                    )}
                    {hideUnsetFields && !invoiceInformation.address?.country ? null : (
                        <InfoField label="Country" value={invoiceInformation.address?.country?.displayName || '-'} />
                    )}
                    <InfoField label="Invoice Email" value={emailValue} />
                    {isCompany && <InfoField label="VAT Number" value={invoiceInformation.vatNumber || '-'} />}
                </div>
            </div>
        </TooltipProvider>
    );
};
